const PlummeAiAdvisorModel = (() => {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
  // Mal for kredittid og selskapsskattesats. Holdes i synk med
  // functions/aiAdvisor/aiAdvisorModel.ts.
  const CREDIT_TARGET_DAYS = 30;
  const COMPANY_TAX_RATE = 0.22;
  const EMPTY_COSTS = [
    { name: 'Ingen kostnader bokført', icon: 'receipt', amount: 0, pct: 0, chg: 0, flag: false },
  ];
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const aiAdvisorT = (key, fallback, vars) => globalThis.t?.(key, fallback, vars) || fallback || key;
  const percentChange = (current, previous) =>
    previous ? Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10 : null;
  const currency = (value) => `${Math.round(num(value)).toLocaleString('nb-NO')} kr`;
  const percentLabel = (value) =>
    value == null || !Number.isFinite(Number(value))
      ? aiAdvisorT('adv.health.badge.none', 'Ikke nok data')
      : `${Number(value) > 0 ? '+' : ''}${Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 1 })} %`;
  const status = (pct) => pct == null ? 'none' : pct >= 0.75 ? 'green' : pct >= 0.45 ? 'amber' : 'red';
  const pctFromMetric = (value, min, max, invert = false) => {
    if (value == null || !Number.isFinite(Number(value))) return 0.5;
    const clamped = Math.min(max, Math.max(min, Number(value)));
    const pct = (clamped - min) / (max - min || 1);
    return invert ? 1 - pct : pct;
  };
  const costIcon = (name = '') => {
    const text = String(name).toLowerCase();
    if (/lønn|lonn|salary|payroll/.test(text)) return 'wallet';
    if (/hus|leie|kontor|lokale|rent/.test(text)) return 'building';
    if (/marked|annonse|reklame|salg/.test(text)) return 'sparkle';
    if (/program|system|lisens|abonnement|software/.test(text)) return 'monitor';
    if (/reise|bil|kjøring|kjoring|transport/.test(text)) return 'car';
    if (/strøm|strom|energi/.test(text)) return 'home';
    return 'receipt';
  };
  const formatRatio = (value) =>
    value == null || !Number.isFinite(Number(value))
      ? aiAdvisorT('adv.cf.notCalculated', 'Ikke beregnet')
      : `${Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 1 })}x`;
  const sectionLines = (report, key) =>
    safeArray(report?.sections).find((section) => section.key === key)?.lines || [];
  const taxItems = ({ settings, home, report }) => {
    const items = [];
    const taxBuffer = num(home?.publicFees?.taxBuffer);
    const vatDue = num(home?.publicFees?.vatDue);
    if (taxBuffer > 0) {
      items.push({
        icon: 'wallet',
        name: 'Sett av buffer til skatt',
        desc: 'Basert på positivt resultat bør du reservere midler før privat uttak eller større kjøp.',
        effekt: currency(taxBuffer),
        besparelse: 'Likviditet',
      });
    }
    if (settings?.vatRegistered && vatDue > 0) {
      items.push({
        icon: 'percent',
        name: 'MVA som må dekkes',
        desc: 'MVA-beløpet er beregnet fra bokførte salg og kjøp. Kontroller terminen før innsending.',
        effekt: currency(vatDue),
        besparelse: 'MVA',
      });
    }
    if (num(report?.resultat) > 0) {
      items.push({
        icon: 'briefcase',
        name: 'Planlagte investeringer',
        desc: 'Vurder om planlagte driftsmidler skal kjøpes før årsslutt, men ikke kjøp bare for å redusere skatt.',
        effekt: 'Vurderes',
        besparelse: 'Ikke beregnet',
      });
    }
    items.push({
      icon: 'receipt',
      name: 'Bilag og fradrag',
      desc: 'Manglende bilag og feil MVA-behandling kan gi svake rapporter. Bruk dette som sjekkliste før levering.',
      effekt: 'Kontroll',
      besparelse: 'Kvalitet',
    });
    return items.slice(0, 4);
  };

  const resolveLiquidityBuffer = ({ settings = {}, costs = 0, taxBuffer = 0, vatDue = 0 } = {}) => {
    const monthlyCosts = Math.max(0, Math.round(num(costs) / 12));
    const recommendedAmount = Math.max(50000, monthlyCosts, Math.round(num(taxBuffer)), Math.round(num(vatDue)));
    const saved = settings.aiAdvisorLiquidityBuffer || {};
    const customAmount = Math.round(num(saved.amount));
    const custom = saved.mode === 'custom' && customAmount > 0;
    return {
      mode: custom ? 'custom' : 'automatic',
      amount: custom ? customAmount : recommendedAmount,
      recommendedAmount,
      basis: {
        minimum: 50000,
        monthlyCosts,
        taxReserve: Math.max(0, Math.round(num(taxBuffer))),
        vatDue: Math.max(0, Math.round(num(vatDue))),
      },
    };
  };

  const buildAiAdvisorModel = ({
    year,
    home,
    report,
    previousReport,
    previousYearAvailable,
    cashBasis = null,
    settings = {},
    customerConcentration = null,
    partyInsights = null,
    state = {},
  } = {}) => {
    if (!home || !report) return null;
    const income = num(report.inntekter);
    const costs = num(report.kostnader);
    const result = num(report.resultat);
    const previousIncome = num(previousReport?.inntekter);
    const previousCosts = num(previousReport?.kostnader);
    const previousResult = num(previousReport?.resultat);
    const hasPreviousYear = previousYearAvailable ?? previousReport?.dataAvailability?.hasAccountingData ?? Boolean(previousIncome || previousCosts || previousResult);
    const incomeYoy = percentChange(income, previousIncome);
    const costsYoy = percentChange(costs, previousCosts);
    const resultMargin = income > 0 ? (result / income) * 100 : null;
    const liquidityRatio = home.analysis?.liquidityRatio;
    const liquidityPct = liquidityRatio == null ? null : pctFromMetric(liquidityRatio, 0, 1.5);
    const growthPct = incomeYoy == null ? null : pctFromMetric(incomeYoy, -20, 25);
    const marginPct = resultMargin == null ? null : pctFromMetric(resultMargin, -15, 25);
    const stabilityPct = customerConcentration == null ? null : pctFromMetric(customerConcentration, 20, 80, true);
    const categories = [
      {
        key: 'likviditet',
        name: aiAdvisorT('adv.health.category.likviditet', 'Likviditet'),
        val: formatRatio(liquidityRatio),
        pct: liquidityPct,
        status: status(liquidityPct),
        note: liquidityRatio == null ? aiAdvisorT('adv.health.note.noOpenPayables', 'Ingen åpne leverandørkrav registrert') : aiAdvisorT('adv.health.note.receivablesVsPayables', 'Kundefordringer målt mot åpne leverandørkrav'),
      },
      {
        key: 'vekst',
        name: aiAdvisorT('adv.health.category.vekst', 'Vekst'),
        val: percentLabel(incomeYoy),
        pct: growthPct,
        status: status(growthPct),
        note: incomeYoy == null ? (hasPreviousYear ? aiAdvisorT('adv.health.note.zeroPrevYearRevenue', 'Fjorårets omsetning er 0; prosentvis vekst kan ikke beregnes') : aiAdvisorT('adv.health.note.needsPrevYear', 'Trenger fjorårstall for sammenligning')) : aiAdvisorT('adv.health.note.revenueChangeVsPrevYear', 'Omsetningsendring mot samme år i fjor'),
      },
      {
        key: 'lonnsomhet',
        name: aiAdvisorT('adv.health.category.lonnsomhet', 'Lønnsomhet'),
        val: percentLabel(resultMargin),
        pct: marginPct,
        status: status(marginPct),
        note: income > 0 ? aiAdvisorT('adv.health.note.marginFromReport', 'Resultatmargin fra resultatrapporten') : aiAdvisorT('adv.health.note.noBookedRevenue', 'Ingen bokført omsetning i valgt år'),
      },
      {
        key: 'stabilitet',
        name: aiAdvisorT('adv.health.category.stabilitet', 'Stabilitet'),
        val: customerConcentration == null ? aiAdvisorT('adv.health.note.unknown', 'Ukjent') : percentLabel(customerConcentration),
        pct: stabilityPct,
        status: status(stabilityPct),
        note: customerConcentration == null ? aiAdvisorT('adv.health.note.needsInvoicesPerCustomer', 'Trenger fakturasalg per kunde') : aiAdvisorT('adv.health.note.largestCustomerShare', 'Andel av salg fra største kunde'),
      },
    ];
    const score = categories.every((item) => item.pct != null) ? Math.round(categories.reduce((sum, item) => sum + (item.pct ?? 0), 0) / categories.length * 100) : null;
    const recommendations = [];
    if (num(home.overview?.salesOverdue) > 0) {
      recommendations.push({
        id: 'overdueSales',
        lvl: 'imp',
        badge: 'Viktig',
        icon: 'clock',
        text: `Forfalte kundefakturaer på ${currency(home.overview.salesOverdue)} bør følges opp.`,
        impactLbl: 'Utestående',
        impact: currency(home.overview.salesOverdue),
        // Bokforte fakturaer som har passert forfall — belopet er bekreftet, ikke anslatt.
        effectType: 'liquidity',
        confidence: 'high',
        action: 'Gå til salg',
        route: 'fakturaliste',
      });
    }
    if (settings?.vatRegistered && num(home.publicFees?.vatDue) > 0) {
      recommendations.push({
        id: 'vatDue',
        lvl: 'crit',
        badge: 'Kritisk',
        icon: 'percent',
        text: `Beregnet MVA å betale er ${currency(home.publicFees.vatDue)}.`,
        impactLbl: 'MVA',
        impact: currency(home.publicFees.vatDue),
        // Effekten er unngatt forsinkelsesrente/gebyr, ikke en inntekt. Belopet er
        // beregnet fra terminen sa langt og kan endre seg for forfall.
        effectType: 'risk',
        confidence: 'medium',
        action: 'Se innleveringer',
        route: 'innsendinger',
      });
    }
    if (num(home.followup?.missingAttachmentCount) > 0) {
      recommendations.push({
        id: 'missingVouchers',
        lvl: 'imp',
        badge: 'Viktig',
        icon: 'receipt',
        text: `${home.followup.missingAttachmentCount} kjøp mangler bilag og bør ryddes før rapportering.`,
        impactLbl: 'Bilag',
        impact: String(home.followup.missingAttachmentCount),
        // Antallet er en fasit, men hvor mye fradrag som faktisk sikres avhenger av
        // hva bilagene viser nar de kommer inn.
        effectType: 'tax',
        confidence: 'medium',
        action: 'Gå til kjøp',
        route: 'allebilag',
      });
    }
    // Opportunities remain separate from ranked tasks.
    const opportunities = [];
    if (costsYoy != null && costsYoy > 15) {
      opportunities.push({
        id: 'costGrowth',
        priority: 70,
        lvl: 'opp',
        badge: 'Mulighet',
        icon: 'sparkle',
        text: `Kostnadene er ${percentLabel(costsYoy)} mot fjoråret. Sjekk de største postene.`,
        impactLbl: 'Endring',
        impact: percentLabel(costsYoy),
        // Avledet av fjorarssammenligning — sier hvor mye kostnaden vokser, ikke hvor
        // mye som faktisk kan kuttes.
        effectType: 'cost',
        confidence: 'medium',
        action: 'Se kostnader',
        route: 'resultatregnskap',
      });
    }
    // Only an independently reconciled cash basis can support a cash curve.
    const cashMonths = cashBasis?.source === 'reconciled-journal-cash' && cashBasis.year === year && Number.isFinite(cashBasis.opening)
      && Array.isArray(cashBasis.months) && cashBasis.months.length === 12
      && cashBasis.months.every((month) => ['balance', 'inn', 'ut'].every(key => typeof month[key] === 'number' && Number.isFinite(month[key])))
      ? cashBasis.months : []
    const now = new Date()
    const currentMonth = year < now.getFullYear() ? 11 : year > now.getFullYear() ? -1 : now.getMonth()
    const startingBalance = cashMonths.length ? (currentMonth < 0 ? cashBasis.opening : cashMonths[currentMonth].balance) : null
    const forecast = cashMonths.map((month, index) => ({
      ...month, m: MONTHS[index] || String(index + 1),
      certainty: year < now.getFullYear() || (year === now.getFullYear() && index < now.getMonth()) ? 'confirmed'
        : year === now.getFullYear() && index === now.getMonth() ? 'expected' : 'uncertain',
    }))
    const liquidityBuffer = resolveLiquidityBuffer({
      settings,
      costs,
      taxBuffer: home.publicFees?.taxBuffer,
      vatDue: home.publicFees?.vatDue,
    });
    const buffer = liquidityBuffer.amount;
    const bufferLabel = liquidityBuffer.mode === 'custom' ? 'valgt buffer' : 'anbefalt buffer';
    const upcomingForecast = forecast.filter((item) => item.certainty !== 'confirmed');
    const riskItems = [];
    if (upcomingForecast.some((item) => item.balance < buffer)) {
      riskItems.push({
        level: 'hoy',
        icon: 'trendDown',
        name: 'Lav likviditetsbuffer',
        desc: `Beregnet saldo fra bokførte tall er under ${bufferLabel} på ${currency(buffer)} i en kommende måned.`,
        action: 'Se kontantstrømprognose',
      });
    }
    if (customerConcentration != null && customerConcentration > 40) {
      riskItems.push({
        level: 'middels',
        icon: 'users',
        name: 'Kundeavhengighet',
        desc: `${partyInsights?.customers?.[0]?.name || 'Største kunde'} står for ${percentLabel(customerConcentration)} av fakturasalget.`,
        action: 'Vurder bredere kundebase',
      });
    }
    const topSupplier = partyInsights?.suppliers?.[0];
    if (topSupplier?.shareBasis === 'yearCosts' && num(topSupplier.sharePct) > 40) {
      riskItems.push({
        level: 'middels',
        icon: 'receipt',
        name: 'Leverandøravhengighet',
        desc: `${topSupplier.name} står for ${percentLabel(topSupplier.sharePct)} av de bokførte kostnadene.`,
        action: 'Vurder leverandøravhengighet',
      });
    }
    if (costsYoy != null && costsYoy > 15) {
      riskItems.push({
        level: 'middels',
        icon: 'trendUp',
        name: 'Økende kostnader',
        desc: `Kostnadene har økt ${percentLabel(costsYoy)} mot fjoråret.`,
        action: 'Gjennomgå kostnader',
      });
    }
    if (num(home.publicFees?.rows?.length) > 0) {
      riskItems.push({
        level: 'lav',
        icon: 'percent',
        name: 'Kommende offentlige krav',
        desc: `SuperPlumma har funnet ${home.publicFees.rows.length} åpne skatte-, MVA- eller lønnsrelaterte krav.`,
        action: 'Se frister',
      });
    }
    const costLines = sectionLines(report, 'kostnader');
    const previousCostLines = new Map(sectionLines(previousReport, 'kostnader').map((line) => [String(line.nr), line]));
    const maxCost = Math.max(1, ...costLines.map((line) => Math.abs(num(line.amount))));
    const costsList = [...costLines]
      .sort((a, b) => Math.abs(num(b.amount)) - Math.abs(num(a.amount)))
      .slice(0, 5)
      .map((line) => {
        const amount = Math.abs(num(line.amount));
        const previous = previousCostLines.get(String(line.nr));
        const chg = previous ? percentChange(amount, Math.abs(num(previous.amount))) : null;
        return {
          name: line.name || line.nr || 'Kostnad',
          icon: costIcon(line.name),
          amount,
          pct: amount / maxCost,
          chg: chg == null ? 0 : Math.round(chg),
          flag: chg != null && chg > 25,
        };
      });
    // `basis` sier hva hver enkelt linje faktisk sammenlignes mot. To av dem er
    // fjorarstall, to er faste terskler — uten a si det ser «0 %» ut som et
    // bransjesnitt i stedet for en nullstrek.
    const bench = [
      { name: 'Omsetningsvekst', you: incomeYoy || 0, available: incomeYoy != null, avg: 0, youL: percentLabel(incomeYoy), avgL: '0 %', better: 'high', max: 30, basis: 'threshold' },
      { name: 'Likviditetsgrad', you: liquidityRatio || 0, available: liquidityRatio != null, avg: 1, youL: formatRatio(liquidityRatio), avgL: '1,0x', better: 'high', max: 2, basis: 'threshold' },
      { name: 'Kostnadsandel', you: income > 0 ? (costs / income) * 100 : 0, avg: previousIncome > 0 ? (previousCosts / previousIncome) * 100 : 0, youL: percentLabel(income > 0 ? (costs / income) * 100 : null), avgL: percentLabel(previousIncome > 0 ? (previousCosts / previousIncome) * 100 : null), better: 'low', max: 100, basis: 'previousYear' },
      { name: 'Resultatmargin', you: resultMargin || 0, avg: previousIncome > 0 ? (previousResult / previousIncome) * 100 : 0, youL: percentLabel(resultMargin), avgL: percentLabel(previousIncome > 0 ? (previousResult / previousIncome) * 100 : null), better: 'high', max: 35, basis: 'previousYear' },
    ];
    // A cash total may include restricted accounts; it does not prove investable funds.
    const heroCapital = 0;
    // ---- Muligheter som trenger buffer, prognose eller fjorårstall ----
    // Holdes i synk med functions/aiAdvisor/aiAdvisorModel.ts.
    const paymentDays = globalThis.PlummeAiAdvisorMonitoring?.averagePaymentDays?.(state, year) || null;
    if (paymentDays && paymentDays.days > CREDIT_TARGET_DAYS + 5 && income > 0) {
      const released = Math.round(income * (paymentDays.days - CREDIT_TARGET_DAYS) / 365);
      if (released > 0) {
        opportunities.push({
          id: 'creditDays',
          priority: 92,
          lvl: 'opp',
          badge: 'Mulighet',
          icon: 'clock',
          text: `Kundene bruker i snitt ${paymentDays.days} dager på å betale (${paymentDays.count} betalte fakturaer). Ned mot ${CREDIT_TARGET_DAYS} dager frigjør rundt ${currency(released)} i arbeidskapital.`,
          impactLbl: 'Frigjort kapital',
          impact: currency(released),
          // Snittdagene er malt pa faktiske betalinger, men belopet skalerer bokfort
          // omsetning sa langt i aret — det er et anslag, ikke en fasit.
          effectType: 'liquidity',
          confidence: 'medium',
          action: 'Gå til salg',
          route: 'fakturaliste',
        });
      }
    }
    // Fremforbart underskudd gjelder bare der satsen faktisk er selskapsskatten.
    // For ENK samordnes underskuddet mot personinntekt til en annen sats.
    if (/^(as|asa)$/i.test(settings?.companyTypeCode || '') && previousResult < 0 && result > 0) {
      const offset = Math.min(Math.abs(previousResult), result);
      const saving = Math.round(offset * COMPANY_TAX_RATE);
      if (saving > 0) {
        opportunities.push({
          id: 'lossCarryforward',
          priority: 90,
          lvl: 'opp',
          badge: 'Mulighet',
          icon: 'percent',
          text: `Fjoråret endte med underskudd på ${currency(Math.abs(previousResult))}. Det kan fremføres mot årets overskudd og senker skattegrunnlaget med inntil ${currency(offset)}.`,
          impactLbl: 'Skatteeffekt',
          impact: currency(saving),
          // Bygger pa bokforte resultater, men endelig fremforing avhenger av
          // skattemessige justeringer regnskapsforer gjor ved arsavslutning.
          effectType: 'tax',
          confidence: 'medium',
          action: 'Se årsavslutning',
          route: 'arsavslutning',
        });
      }
    }
    const budget = globalThis.PlummeAiAdvisorMonitoring?.budgetSignals?.({ state, year, report, now: Date.now() }) || {};
    if (budget.incomeVariancePct != null && budget.incomeVariancePct > 15) opportunities.push({ id: 'incomeAboveBudget', priority: 78, lvl: 'opp', badge: 'Mulighet', icon: 'trendUp', text: `Omsetningen ligger ${percentLabel(budget.incomeVariancePct)} over budsjettet hittil i år. Kapasiteten er der — vurder om budsjettet, prisene eller bemanningen bør justeres opp.`, impactLbl: 'Over budsjett', impact: percentLabel(budget.incomeVariancePct), effectType: 'income', confidence: 'high', action: 'Se budsjett', route: 'budsjett' });
    opportunities.push(...globalThis.PlummeAiAdvisorOpportunities.build({ state, year, partyInsights, costs, currency }));
    opportunities.sort((a, b) => num(b.priority) - num(a.priority) || a.id.localeCompare(b.id));
    const extraTasks = globalThis.PlummeAiAdvisorTasks.buildAiAdvisorTasks({ year, state, home, report, previousReport, forecast, buffer, costsList, partyInsights, budget, paymentDays });
    const rankedTasks = [...recommendations.map((rec) => ({ ...rec, priority: rec.id === 'vatDue' ? 96 : rec.id === 'overdueSales' ? 95 : 88 })), ...extraTasks].map((rec) => ({ ...rec, year })).sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));
    // Statusmelding, ikke en oppside: den skal bare stå der når det ikke finnes noe
    // ekte å vise, ellers konkurrerer «alt ser bra ut» med faktiske funn.
    if (!rankedTasks.length && !opportunities.length) {
      opportunities.push({
        id: 'allClear',
        priority: 0,
        lvl: 'opp',
        badge: 'Mulighet',
        icon: 'sparkle',
        text: 'Tallene ser ryddige ut. Neste steg er å følge opp kontantstrøm, bilag og kommende frister.',
        impactLbl: 'Status',
        impact: 'OK',
        // Ingen tallfestet effekt a merke — dette er en statusmelding, ikke et tiltak.
        effectType: null,
        confidence: 'low',
        action: 'Se analyse',
        route: null,
      });
    }
    const finalCosts = costsList.length ? costsList : EMPTY_COSTS;
    const yearKey = String(year);
    const importedYears = safeArray(state.accountingImportArchiveSummary?.importYears).map(String);
    const voucherCoverageKnown = !importedYears.includes(yearKey) &&
      !state.accountingImportReportStatsByYear?.[yearKey];
    // Keep the local fallback aligned with the backend narrative.
    const narrative = globalThis.PlummeAiAdvisorNarrative?.buildAdvisorNarrative?.({
      income,
      costs,
      result,
      previousIncome,
      previousYearAvailable: hasPreviousYear,
      costsYoy,
      concentration: customerConcentration,
      home,
      forecast,
      buffer,
      costsList: finalCosts,
      bench,
      recommendations: [...rankedTasks, ...opportunities],
      freeCapital: heroCapital,
      voucherCoverageKnown,
      invoices: state.invoices, year,
    }) || null;
    const monitor = globalThis.PlummeAiAdvisorMonitoring?.buildAiAdvisorMonitoring?.({
      state, year, report, previousReport, home, partyInsights, forecast, buffer,
    }) || narrative?.monitor || [];
    const generatedAt = new Date().toISOString();
    const finalRecommendations = narrative?.recommendations
      || [...rankedTasks, ...opportunities];
    // Morgenbrief, nattarbeid og hendelsesstrøm. Bygges etter monitor og anbefalinger,
    // fordi nattarbeidet teller nettopp de kontrollene og rådene som faktisk ble laget.
    const briefing = globalThis.PlummeAiAdvisorBriefing?.buildAdvisorBriefing?.({
      state,
      year,
      home,
      report,
      settings,
      monitor,
      recommendations: [...rankedTasks.slice(0, 6), ...opportunities.slice(0, 4)],
      costsList: finalCosts,
      income,
      incomeYoy,
      costsYoy,
      resultMargin,
      previousResultMargin: previousIncome > 0 ? (previousResult / previousIncome) * 100 : null,
      concentration: customerConcentration,
      generatedAt,
    }) || null;
    return {
      source: 'app-state',
      generatedAt,
      year,
      score,
      categories,
      recommendations: finalRecommendations,
      verdicts: narrative?.verdicts,
      why: narrative?.why,
      liquidityWatch: narrative?.liquidityWatch,
      moves: narrative?.moves,
      confidence: narrative?.confidence,
      monitor,
      forecast,
      buffer,
      liquidityBuffer,
      partyInsights,
      risks: riskItems.slice(0, 4),
      costs: finalCosts,
      tax: taxItems({ settings, home, report }),
      bench,
      summaryText: score == null ? 'Vurderingen er foreløpig. Manglende nøkkeltall inngår ikke i en samlet helsescore.' : score >= 80
        ? 'Bedriften har en solid økonomisk helsescore basert på bokførte tall. Følg spesielt med på kontantstrøm, åpne krav og kostnadsposter som vokser raskt.'
        : score >= 60
          ? 'Bedriften er stabil, men har noen forhold som bør følges opp før de blir større problemer.'
          : 'Bedriften trenger tettere oppfølging. Start med likviditet, forfalte poster og dokumentasjon.',
      cashflowCommentary: !cashMonths.length ? aiAdvisorT('adv.cf.cashBasisMissing') : upcomingForecast.some((item) => item.balance < buffer)
        ? `Beregnet saldo fra bokførte tall kan falle under ${bufferLabel} på ${currency(buffer)}. Se på forfalte krav og planlagte betalinger.`
        : upcomingForecast.length
          ? `Beregnet saldo fra bokførte tall ligger over ${bufferLabel} på ${currency(buffer)} i kommende måneder.`
          : 'Ingen kommende måneder med beregnet saldo i valgt periode.',
      hero: {
        balance: startingBalance,
        buffer,
        freeCapital: heroCapital,
        text: startingBalance == null ? aiAdvisorT('adv.cf.cashBasisMissing') : aiAdvisorT('adv.cf.cashBalanceEvidence', '', { amount: currency(startingBalance) }),
      },
      // Tallene resten av rådgiveren regner videre på. Uten dem må hver fane utlede
      // resultatet på nytt, og da kan to faner vise ulikt resultat for samme år.
      financials: { income: Math.round(income), costs: Math.round(costs), result: Math.round(result), resultMargin },
      overnight: briefing?.overnight || null,
      heroEffects: briefing?.effects || [],
      feed: briefing?.feed || [],
      employees: briefing?.employees || null,
    };
  };

  return { buildAiAdvisorModel };
})();

Object.assign(globalThis, { PlummeAiAdvisorModel });
